import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { interval, Subject, Subscription } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { AppConfig } from '../models/app-config';
import { BackendAPI } from '../models/backend-api';
import { LogRecord } from '../models/log-record';
import { AppConfigService } from './app-config.service';

export enum LogLevel {
	Fatal = 0,
	Error,
	Warning,
	Info,
	Debug
}

const priorityDebounceInterval = 3000;
const usualDebounceInterval = 15000;

@Injectable({
	providedIn: 'root'
})
export class Logger implements OnDestroy {

	private COMPONENT_NAME = "Logger";

	private uploadLogsSubject = new Subject<LogLevel>();
	private subscription: Subscription = Subscription.EMPTY;
	private failedUploadsCounter = 0;
	private logs: LogRecord[] = [];
	private consoleLogLevel: LogLevel | undefined = undefined;

	constructor (private appConfigService: AppConfigService, private httpClient: HttpClient) {
		this.subscription = this.uploadLogsSubject.pipe(
			debounce((logLevel: LogLevel) => {
				switch (logLevel) {
					case LogLevel.Fatal:
					case LogLevel.Error:
					case LogLevel.Warning:
						return interval(priorityDebounceInterval);
					default:
						return interval(usualDebounceInterval);
				}
			}))
			.subscribe(() => {
				this.uploadLogs();
			});

		const configChagedSubscription = this.appConfigService.config$.subscribe((config: AppConfig | undefined) => {
			if (config && config.consoleLogLevel) {
				switch (config.consoleLogLevel.toUpperCase()) {
					case "FATAL":
						this.consoleLogLevel = LogLevel.Fatal;
						break;
					case "ERROR":
						this.consoleLogLevel = LogLevel.Error;
						break;
					case "WARNING":
						this.consoleLogLevel = LogLevel.Warning;
						break;
					case "INFO":
						this.consoleLogLevel = LogLevel.Info;
						break;
					case "DEBUG":
						this.consoleLogLevel = LogLevel.Debug;
						break;
					default:
						this.error(this.COMPONENT_NAME, `Cannot convert string value of consoleLogLevel into LogLevel. Value="${config.consoleLogLevel}"`);
						break;
				}
			}
		});

		this.subscription.add(configChagedSubscription);
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

	error(subsystem: string, message: string, error: any | undefined = undefined): void {
		const details: string[] = [];
		details.push(subsystem);
		details.push(message);

		if (error && error.error instanceof ErrorEvent) {
			// client-side error
			details.push(`Message: ${error.error.message}`);
		}
		else if (error) {
			if (error.message) {
				details.push(`Message: ${error.message}`);
			}
			if (error.errorMessage) {
				details.push(`Message: ${error.errorMessage}`);
			}
			if (error.name) {
				details.push(`Name: ${error.name}`);
			}
			if (error.status !== undefined) {
				details.push(`Status: ${error.status}`);
			}
			if (error.statusText) {
				details.push(`Status Text: ${error.statusText}`);
			}
			if (error.url) {
				details.push(`URL: ${error.url}`);
			}
		}

		const logMessage = details.join(" | ");
		this.addLogRecord(LogLevel.Error, subsystem, logMessage);

		if (!environment.production || (this.consoleLogLevel && this.consoleLogLevel >= LogLevel.Error)) {
			console.log("%cError | %s", "color: red", logMessage);
		}
	}

	warning(subsystem: string, message: string): void {
		this.addLogRecord(LogLevel.Info, subsystem, message);
		if (!environment.production || (this.consoleLogLevel && this.consoleLogLevel >= LogLevel.Warning)) {
			console.log("%cWarning | %s | %s", "color: OrangeRed", subsystem, message);
		}
	}

	info(subsystem: string, message: string): void {
		this.addLogRecord(LogLevel.Info, subsystem, message);
		if (!environment.production || (this.consoleLogLevel && this.consoleLogLevel >= LogLevel.Info)) {
			console.log("%cInfo | %s | %s", "color: blue", subsystem, message);
		}
	}

	debug(subsystem: string, message: string): void {

		this.addLogRecord(LogLevel.Debug, subsystem, message);
		if (!environment.production || (this.consoleLogLevel && this.consoleLogLevel >= LogLevel.Debug)) {
			console.log("%cDebug | %s | %s", "color: green", subsystem, message);
		}
	}

	private addLogRecord(level: LogLevel, component: string, message: string) {
		const now = new Date();
		const logRecord: LogRecord = {
			level: level as number,
			clientTime: now.toISOString(),
			component: component,
			message: message
		}

		// limit the logs collection size
		if (this.logs.length > 1000) {
			this.logs.shift();
		}

		this.logs.push(logRecord);

		this.uploadLogsSubject.next(level);
	}

	public uploadLogs(): void {

		if (this.logs.length === 0) {
			// Logs are empty
			return;
		}

		if (this.failedUploadsCounter > 5) {
			// All previous upload attempts failed. Stopping...
			return;
		}

		const url = BackendAPI.systemLog;

		const uploadingLogs = this.logs;
		this.logs = [];

		this.httpClient.post<LogRecord[]>(url, uploadingLogs).subscribe({
			next: () => {
				this.failedUploadsCounter = 0;
			},
			error: () => {
				++this.failedUploadsCounter;
				// restore logs in hope it succeeds next time
				const freshLogs = this.logs;
				this.logs = uploadingLogs;     // put back the old records
				this.logs.push(...freshLogs); //... and append those added during the web-call
			}
		});
	}
}
